<template>
  <section v-if="updateAvailable" class="app-update-banner" role="status" aria-live="polite">
    <span>{{ language.t('app.updateAvailable') }}</span>
    <button type="button" @click="refreshApp">{{ language.t('app.refreshNow') }}</button>
  </section>
  <LoginView v-if="!session.isAuthenticated" />
  <ChatShell v-else />
  <LanguageSwitcher v-if="session.isAuthenticated" />
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LoginView from './components/LoginView.vue';
import ChatShell from './components/ChatShell.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import { useSessionStore } from './stores/session';
import { useConversationStore } from './stores/conversations';
import { useLanguageStore } from './stores/language';
import { hasNewAppVersion } from './utils/appVersion.mjs';
import { appConfig } from './config';

const session = useSessionStore();
const conversations = useConversationStore();
const language = useLanguageStore();
const updateAvailable = ref(false);
let updateCheckTimer = null;

function currentAppAssetUrl() {
  return Array.from(document.scripts)
    .map(script => script.src)
    .find(source => /\/js\/app\.[^/]+\.js(?:[?#]|$)/i.test(source)) || '';
}

async function checkForAppUpdate() {
  if (appConfig.capacitorApp || updateAvailable.value) {
    return;
  }
  try {
    const url = new URL('index.html', document.baseURI);
    url.searchParams.set('update_check', Date.now().toString());
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (response.ok) {
      updateAvailable.value = hasNewAppVersion(currentAppAssetUrl(), await response.text());
    }
  } catch {
    // A failed version check must not interrupt login or messaging.
  }
}

function checkWhenVisible() {
  if (document.visibilityState === 'visible') {
    checkForAppUpdate();
  }
}

function refreshApp() {
  window.location.reload();
}

onMounted(() => {
  language.restore();
  session.restore();
  checkForAppUpdate();
  updateCheckTimer = window.setInterval(checkForAppUpdate, 60 * 1000);
  window.addEventListener('focus', checkForAppUpdate);
  document.addEventListener('visibilitychange', checkWhenVisible);
});

onBeforeUnmount(() => {
  if (updateCheckTimer) {
    window.clearInterval(updateCheckTimer);
  }
  window.removeEventListener('focus', checkForAppUpdate);
  document.removeEventListener('visibilitychange', checkWhenVisible);
});

watch(() => session.userId, userId => {
  if (userId && session.isAuthenticated) {
    conversations.bootstrapDemoData();
    return;
  }
  conversations.clearUserState();
});
</script>
